import type { ReactNode } from 'react'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { OGSM_COPY } from '@/features/ogsm/ogsm-labels'

export interface OgsmTitleRowProps {
  title: string
  /** Drilled view only: the back arrow beside the title. */
  onUpOneLevel?: () => void
  /** Right group: FY select, read-only note, Start FYxx, New objective. */
  children?: ReactNode
  /** `h2` (styled as h3) where the page already has an h1, as in the portal. */
  headingLevel?: 'h1' | 'h2'
}

/** The page title with the session controls on the same row; they wrap under it when narrow. */
export function OgsmTitleRow({
  title,
  onUpOneLevel,
  children,
  headingLevel = 'h1',
}: OgsmTitleRowProps) {
  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-border pb-3">
      <div className="flex min-w-0 grow basis-80 items-center gap-3">
        {onUpOneLevel && (
          <Button
            variant="outline"
            size="icon"
            className="size-9 shrink-0"
            aria-label={OGSM_COPY.upOneLevel}
            title={OGSM_COPY.upOneLevel}
            onClick={onUpOneLevel}
          >
            <ArrowLeft className="size-4" />
          </Button>
        )}
        <Typography
          as={headingLevel}
          variant={headingLevel === 'h1' ? 'h1' : 'h3'}
          className="min-w-0 break-words"
        >
          {title}
        </Typography>
      </div>
      {children && (
        <div className="ml-auto flex flex-wrap items-center gap-3">
          {children}
        </div>
      )}
    </div>
  )
}
